// Shared presentational pieces for the July-14 role Home dashboards (FR-P1/FR-P2). Content only —
// rendered inside the existing top-nav shell (the sidebar in the mockups is out of scope for this
// order; the dashboard CONTENT lives at the Home route).
import { Link } from "react-router-dom";
import type { DashboardPreprintCard } from "@rrid/shared-types";
import { Badge, Card, CardBody, CardHeader, EmptyState, statusTone } from "../../components/ui.js";

/** A single KPI stat card: big value + label + optional sublabel/delta. */
export function KpiCard({
  label,
  value,
  sublabel,
  tone = "slate",
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  tone?: "slate" | "green" | "blue" | "amber" | "violet" | "red";
}): JSX.Element {
  const valueColor: Record<string, string> = {
    slate: "text-slate-800",
    green: "text-green-700",
    blue: "text-blue-700",
    amber: "text-amber-700",
    violet: "text-violet-700",
    red: "text-rose-700",
  };
  return (
    <Card className="h-full">
      <CardBody className="space-y-1">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`text-3xl font-bold ${valueColor[tone]}`}>{value}</div>
        {sublabel && <div className="text-xs text-slate-500">{sublabel}</div>}
      </CardBody>
    </Card>
  );
}

/** A titled column of preprint cards (the "My Preprints" kanban lists). */
export function PreprintColumn({
  title,
  cards,
  tone = "slate",
  showGreenlits = false,
  emptyLabel = "Nothing here yet.",
}: {
  title: string;
  cards: DashboardPreprintCard[];
  tone?: "slate" | "green" | "blue" | "amber" | "violet";
  showGreenlits?: boolean;
  emptyLabel?: string;
}): JSX.Element {
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          {showGreenlits && <span className="text-[10px] uppercase tracking-wide text-slate-400">Greenlits</span>}
          <Badge tone={tone}>{cards.length}</Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-1.5">
        {cards.length === 0 ? (
          <p className="text-xs text-slate-400">{emptyLabel}</p>
        ) : (
          cards.map((c) => <PreprintRow key={(c.preprintId ?? c.selectedId ?? c.title)} card={c} showGreenlits={showGreenlits} />)
        )}
      </CardBody>
    </Card>
  );
}

/**
 * SoftR-parity R-003: the combined To-Pitch column — Team (blue) and Editorial (amber) pitches
 * stacked under one header so both pitch queues read as a single stage with distinct colors.
 */
export function PitchGroup({
  team,
  editorial,
}: {
  team: DashboardPreprintCard[];
  editorial: DashboardPreprintCard[];
}): JSX.Element {
  return (
    <Card className="h-full">
      <CardHeader className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">To-Pitch</h3>
        <Badge tone="slate">{team.length + editorial.length}</Badge>
      </CardHeader>
      <CardBody className="space-y-3">
        <PitchSublist label="Team" tone="blue" cards={team} />
        <PitchSublist label="Editorial" tone="amber" cards={editorial} />
      </CardBody>
    </Card>
  );
}

function PitchSublist({
  label,
  tone,
  cards,
}: {
  label: string;
  tone: "blue" | "amber";
  cards: DashboardPreprintCard[];
}): JSX.Element {
  const accent = tone === "blue" ? "border-l-blue-400" : "border-l-amber-400";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Badge tone={tone}>{label}</Badge>
        <span className="text-xs text-slate-400">{cards.length}</span>
      </div>
      {cards.length === 0 ? (
        <p className="text-xs text-slate-400">No {label.toLowerCase()} pitches.</p>
      ) : (
        <div className={`space-y-1.5 border-l-2 pl-2 ${accent}`}>
          {cards.map((c) => (
            <PreprintRow key={c.preprintId ?? c.selectedId ?? c.title} card={c} showGreenlits={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function PreprintRow({ card, showGreenlits }: { card: DashboardPreprintCard; showGreenlits: boolean }): JSX.Element {
  const to = card.selectedId ? `/preprints/${card.selectedId}` : card.preprintId ? `/reviewers` : "#";
  const inner = (
    <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 p-2 hover:border-brand">
      <span className="truncate text-sm">{card.title}</span>
      {showGreenlits ? (
        <span className="shrink-0 text-sm font-semibold text-green-700">{card.greenlitCount ?? 0}</span>
      ) : (
        <Badge tone={statusTone(card.status)}>{card.status}</Badge>
      )}
    </div>
  );
  return to === "#" ? inner : <Link to={to}>{inner}</Link>;
}

export { EmptyState };
